import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getTodayIsoDate, validateCustomOrderForm } from '../lib/customOrderValidation';

const STATUS_LABELS = {
  submitted: 'Submitted',
  reviewing: 'Reviewing',
  awaiting_customer: 'Awaiting your reply',
  quoted: 'Quote shared',
  approved: 'Approved',
  in_production: 'In production',
  shipped: 'Shipped',
  completed: 'Completed',
  declined: 'Declined',
};

const ACTIVE_REQUEST_STATUSES = new Set([
  'submitted',
  'reviewing',
  'awaiting_customer',
  'quoted',
  'approved',
  'in_production',
  'shipped',
]);

const TIME_FILTERS = [
  { value: 'all', label: 'Any time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'older', label: 'Older than 90 days' },
];

const initialForm = {
  title: '',
  description: '',
  referenceText: '',
  quantity: '1',
  budget: '',
  suggestedDeadline: '',
};

const MAX_REFERENCE_IMAGES = 5;
const MAX_REFERENCE_IMAGE_BYTES = 100 * 1024 * 1024;
const MAX_REFERENCE_TOTAL_BYTES = 100 * 1024 * 1024;
const REFERENCE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const CUSTOM_ORDER_FORM_HIDDEN_KEY = 'hunar.customOrderFormHidden.v1';

function isCustomOrderFormHidden() {
  try {
    return window.localStorage.getItem(CUSTOM_ORDER_FORM_HIDDEN_KEY) === 'true';
  } catch (_error) {
    return false;
  }
}

function setCustomOrderFormHidden(hidden) {
  try {
    if (hidden) window.localStorage.setItem(CUSTOM_ORDER_FORM_HIDDEN_KEY, 'true');
    else window.localStorage.removeItem(CUSTOM_ORDER_FORM_HIDDEN_KEY);
  } catch (_error) {}
}

function getReferenceFileKey(file) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function readReferenceImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      fileKey: getReferenceFileKey(file),
      filename: file.name,
      contentType: file.type,
      dataUrl: String(reader.result || ''),
      size: file.size,
    });
    reader.onerror = () => reject(new Error('One of the images could not be read. Try again.'));
    reader.readAsDataURL(file);
  });
}

function formatMegabytes(bytes) {
  return `${(bytes / (1024 * 1024)).toFixed(bytes % (1024 * 1024) === 0 ? 0 : 1)} MB`;
}

function statusLabel(status) {
  return STATUS_LABELS[status] || String(status || '').replace(/_/g, ' ');
}

function formatDate(value) {
  if (!value) return 'Not provided';
  return new Date(`${value}T00:00:00`).toLocaleDateString();
}

function FieldError({ id, message }) {
  if (!message) return null;
  return <div id={id} className="custom-order-field-error" role="alert">{message}</div>;
}

function RequestCard({ request, adminView, onDelete }) {
  const target = adminView ? `/admin/custom-orders/${request.id}` : `/custom-orders/${request.id}`;
  return (
    <div className="border rounded bg-card p-4 shadow-sm h-100">
      <Link to={target} className="d-block text-reset text-decoration-none">
        <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
          <div>
            <span className="small text-muted">{request.requestNumber}</span>
            <h3 className="h5 brand-font mb-0">{request.title}</h3>
          </div>
          <span className="badge bg-primary align-self-start">{statusLabel(request.status)}</span>
        </div>
        {adminView && <p className="small text-muted mb-2">{request.customerName || request.customerEmail}</p>}
        <p className="text-muted small mb-3">{request.description}</p>
        <div className="small text-muted d-flex flex-column gap-1">
          <span>Quantity: {request.quantity}</span>
          <span>Suggested deadline: {formatDate(request.suggestedDeadline)} <em>(not guaranteed)</em></span>
          <span>{request.messageCount || 0} message{request.messageCount === 1 ? '' : 's'}</span>
        </div>
      </Link>
      {adminView && (
        <div className="border-top mt-3 pt-3">
          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => onDelete(request)}>
            Delete request
          </button>
        </div>
      )}
    </div>
  );
}

export default function CustomOrdersPage({ adminView = false }) {
  const { token, user } = useContext(AuthContext);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [thread, setThread] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingRequestId, setDeletingRequestId] = useState(null);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [referenceImages, setReferenceImages] = useState([]);
  const [imageReading, setImageReading] = useState(false);
  const referenceFileInput = useRef(null);
  const [showRequestForm, setShowRequestForm] = useState(() => (
    !adminView
      && (new URLSearchParams(location.search).get('new') === '1' || !isCustomOrderFormHidden())
  ));

  const basePath = adminView ? '/admin/custom-orders' : '/custom-orders';

  const loadRequests = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/custom-orders', { headers: { 'x-auth-token': token } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || 'Unable to load custom-order requests.');
      setRequests(data.requests || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadThread = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/custom-orders/${id}`, { headers: { 'x-auth-token': token } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || 'Unable to load this custom-order request.');
      setThread(data);
      setStatus(data.request?.status || '');
    } catch (threadError) {
      setError(threadError.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    if (id) {
      loadThread();
    } else {
      setThread(null);
      loadRequests();
    }
  }, [id, loadRequests, loadThread]);

  useEffect(() => {
    if (!adminView && !loading && requests.length === 0 && !isCustomOrderFormHidden()) setShowRequestForm(true);
  }, [adminView, loading, requests.length]);

  useEffect(() => {
    if (!adminView && new URLSearchParams(location.search).get('new') === '1') {
      setCustomOrderFormHidden(false);
      setShowRequestForm(true);
    }
  }, [adminView, location.search]);

  const updateFormField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleReferenceImageChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const selectedKeys = new Set(referenceImages.map((image) => image.fileKey));
    const newFiles = files.filter((file) => !selectedKeys.has(getReferenceFileKey(file)));
    const combinedImages = [...referenceImages, ...newFiles];
    const resetInput = () => {
      if (referenceFileInput.current) referenceFileInput.current.value = '';
    };
    const clearImageError = (current) => {
      const next = { ...current };
      delete next.referenceImage;
      return next;
    };

    setFormErrors(clearImageError);
    if (combinedImages.length > MAX_REFERENCE_IMAGES) {
      setFormErrors((current) => ({ ...current, referenceImage: `Choose up to ${MAX_REFERENCE_IMAGES} images.` }));
      resetInput();
      return;
    }

    const invalidFile = newFiles.find((file) => !REFERENCE_IMAGE_TYPES.has(file.type));
    if (invalidFile) {
      setFormErrors((current) => ({ ...current, referenceImage: 'Upload PNG, JPG, WEBP, or GIF images only.' }));
      resetInput();
      return;
    }

    const oversizedFile = newFiles.find((file) => file.size > MAX_REFERENCE_IMAGE_BYTES);
    if (oversizedFile) {
      setFormErrors((current) => ({ ...current, referenceImage: `Each image must be ${formatMegabytes(MAX_REFERENCE_IMAGE_BYTES)} or smaller.` }));
      resetInput();
      return;
    }

    const combinedBytes = combinedImages.reduce((total, image) => total + image.size, 0);
    if (combinedBytes > MAX_REFERENCE_TOTAL_BYTES) {
      setFormErrors((current) => ({ ...current, referenceImage: `Keep all images together under ${formatMegabytes(MAX_REFERENCE_TOTAL_BYTES)}.` }));
      resetInput();
      return;
    }

    if (!newFiles.length) {
      resetInput();
      return;
    }
    setImageReading(true);
    try {
      const parsedImages = await Promise.all(newFiles.map(readReferenceImage));
      setReferenceImages((current) => [...current, ...parsedImages]);
    } catch (readError) {
      setFormErrors((current) => ({ ...current, referenceImage: readError.message }));
    } finally {
      setImageReading(false);
      if (referenceFileInput.current) referenceFileInput.current.value = '';
    }
  };

  const removeReferenceImage = (fileKey) => {
    setReferenceImages((current) => current.filter((image) => image.fileKey !== fileKey));
    setFormErrors((current) => {
      const next = { ...current };
      delete next.referenceImage;
      return next;
    });
    if (referenceFileInput.current) referenceFileInput.current.value = '';
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const validation = validateCustomOrderForm(form);
    const nextErrors = { ...validation.errors };
    if (formErrors.referenceImage) nextErrors.referenceImage = formErrors.referenceImage;
    setFormErrors(nextErrors);
    setError('');
    if (Object.keys(nextErrors).length > 0 || imageReading) return;

    setSaving(true);
    try {
      const res = await fetch('/api/custom-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify({
          ...validation.normalized,
          files: referenceImages,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || 'Unable to create your request.');
      setForm(initialForm);
      setReferenceImages([]);
      if (referenceFileInput.current) referenceFileInput.current.value = '';
      setFormErrors({});
      navigate(`${basePath}/${data.request.id}`);
    } catch (createError) {
      setError(createError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!message.trim() || !id) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`/api/custom-orders/${id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify({ body: message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || 'Unable to send your message.');
      setThread((current) => ({ ...current, messages: [...(current?.messages || []), data.message] }));
      setMessage('');
    } catch (messageError) {
      setError(messageError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async () => {
    if (!id || !status) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`/api/custom-orders/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify({ status }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || 'Unable to update the request status.');
      await loadThread();
    } catch (statusError) {
      setError(statusError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequest = async (request) => {
    if (!adminView || !request?.id || deletingRequestId) return;
    const requestLabel = [request.requestNumber, request.title].filter(Boolean).join(' — ');
    const confirmed = window.confirm(
      `Delete ${requestLabel || 'this custom-order request'}? This permanently removes the request, conversation, uploaded reference images, and notifications.`
    );
    if (!confirmed) return;

    setDeletingRequestId(Number(request.id));
    setError('');
    try {
      const res = await fetch(`/api/custom-orders/${request.id}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || 'Unable to delete the custom-order request.');

      if (id && Number(id) === Number(request.id)) {
        navigate(basePath, { replace: true });
      } else {
        setRequests((current) => current.filter((item) => Number(item.id) !== Number(request.id)));
      }
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setDeletingRequestId(null);
    }
  };

  const filteredRequests = requests.filter((request) => {
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && ACTIVE_REQUEST_STATUSES.has(request.status)) ||
      request.status === statusFilter;

    if (!matchesStatus || timeFilter === 'all') return matchesStatus;

    const createdAt = new Date(request.createdAt).getTime();
    if (!Number.isFinite(createdAt)) return true;

    const olderThan = Date.now() - (Number(timeFilter) || 90) * 24 * 60 * 60 * 1000;
    return timeFilter === 'older' ? createdAt < olderThan : createdAt >= olderThan;
  });

  if (!user && !token) {
    return (
      <div className="container py-5 text-center">
        <h1 className="brand-font">Custom orders</h1>
        <p className="text-muted">Sign in with Google to start or continue a private custom-order conversation.</p>
        <Link to="/login" className="btn btn-primary-custom rounded-pill">Sign in with Google</Link>
      </div>
    );
  }

  if (loading) {
    return <div className="container text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div></div>;
  }

  if (thread) {
    return (
      <div className="container py-4">
        <button type="button" className="btn btn-link text-reset text-decoration-none px-0 mb-3" onClick={() => navigate(basePath)}>
          ← Back to custom orders
        </button>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <span className="small text-muted">{thread.request.requestNumber}</span>
            <h1 className="brand-font mb-1">{thread.request.title}</h1>
            <p className="text-muted mb-0">Preferred completion date: {formatDate(thread.request.suggestedDeadline)} <em>(we will confirm timing)</em></p>
          </div>
          <span className="badge bg-primary fs-6">{statusLabel(thread.request.status)}</span>
        </div>

        {adminView && (
          <div className="border rounded bg-card p-3 mb-4">
            <div className="small text-muted mb-1">Customer</div>
            <div className="fw-semibold">{thread.request.customerName || thread.request.customerEmail}</div>
            <div className="small text-muted">{thread.request.customerEmail}</div>
            <div className="d-flex flex-wrap align-items-end gap-2 mt-3">
              <label className="small fw-semibold" htmlFor="custom-order-status">Update status
                <select id="custom-order-status" className="form-select mt-1" value={status} onChange={(event) => setStatus(event.target.value)}>
                  {(thread.statuses || Object.keys(STATUS_LABELS)).map((option) => <option key={option} value={option}>{statusLabel(option)}</option>)}
                </select>
              </label>
              <button type="button" className="btn btn-primary-custom" onClick={handleStatusChange} disabled={saving || status === thread.request.status}>Save status</button>
              <button type="button" className="btn btn-outline-danger" onClick={() => handleDeleteRequest(thread.request)} disabled={saving || deletingRequestId === Number(thread.request.id)}>
                {deletingRequestId === Number(thread.request.id) ? 'Deleting…' : 'Delete request'}
              </button>
            </div>
            <div className="small text-muted mt-2">Choose Approved when you accept the request or Declined when the budget does not work. The customer will be notified.</div>
          </div>
        )}

        <div className="border rounded bg-card p-4 shadow-sm mb-4">
          <h2 className="h5 brand-font">Request details</h2>
          <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{thread.request.description}</p>
          <div className="small text-muted d-flex flex-column gap-1">
            <span>Quantity: {thread.request.quantity}</span>
            {thread.request.budget !== null && thread.request.budget !== undefined && (
              <span>Budget: ₹{thread.request.budget}</span>
            )}
            {thread.request.referenceText && <span>Reference: {thread.request.referenceText}</span>}
          </div>
          {thread.attachments?.length > 0 && (
            <div className="custom-order-attachments mt-4">
              <h3 className="h6 brand-font mb-2">Reference image</h3>
              <div className="custom-order-attachments__grid">
                {thread.attachments.map((attachment) => (
                  <a key={attachment.id} href={attachment.url} target="_blank" rel="noreferrer" className="custom-order-attachment">
                    <img src={attachment.url} alt={attachment.filename} />
                    <span>{attachment.filename}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        <div className="d-flex flex-column gap-3 mb-4">
          {thread.messages.map((item) => (
            <div key={item.id} className={`border rounded p-3 ${item.senderRole === 'admin' ? 'bg-background ms-md-5' : 'bg-card me-md-5'}`}>
              <div className="d-flex justify-content-between gap-2 mb-1">
                <strong>{item.senderRole === 'admin' ? 'Hunar team' : item.senderName}</strong>
                <span className="small text-muted">{new Date(item.createdAt).toLocaleString()}</span>
              </div>
              <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{item.body}</p>
            </div>
          ))}
        </div>

        <form onSubmit={handleSendMessage} className="border rounded bg-card p-4 shadow-sm">
          <label htmlFor="custom-order-message" className="form-label fw-semibold">Reply in this conversation</label>
          <textarea id="custom-order-message" className="form-control mb-2" rows="4" value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Ask a question or share another detail…" maxLength={4000} />
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="form-text mb-0">{message.length}/4000 characters</div>
            <button type="submit" className="btn btn-primary-custom" disabled={saving || !message.trim()}>{saving ? 'Sending…' : 'Send update'}</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="mb-4">
        <h1 className="brand-font mb-1">{adminView ? 'Custom-order requests' : 'Custom orders'}</h1>
        <p className="text-muted mb-0">{adminView ? 'Review requests and continue each customer conversation.' : 'Tell us what you would like made. A preferred completion date helps us plan, and the Hunar team will confirm the timing with you.'}</p>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      {!adminView && (
        <>
          <div className="d-flex justify-content-end mb-3">
            <button
              type="button"
              className="btn btn-outline-custom rounded-pill"
              aria-expanded={showRequestForm}
              aria-controls="custom-order-request-form"
              onClick={() => setShowRequestForm((current) => {
                const next = !current;
                setCustomOrderFormHidden(!next);
                return next;
              })}
            >
              {showRequestForm ? 'Hide request form' : '＋ New custom-order request'}
            </button>
          </div>
          {showRequestForm && (
            <form id="custom-order-request-form" onSubmit={handleCreate} noValidate className="border rounded bg-card p-4 shadow-sm mb-5">
              <h2 className="h5 brand-font mb-3">Start a request</h2>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label" htmlFor="custom-order-title">What would you like made? *</label>
              <input id="custom-order-title" className="form-control" value={form.title} onChange={(event) => updateFormField('title', event.target.value)} maxLength={160} required aria-invalid={formErrors.title ? 'true' : undefined} aria-describedby={formErrors.title ? 'custom-order-title-error' : undefined} />
              <FieldError id="custom-order-title-error" message={formErrors.title} />
            </div>
            <div className="col-12">
              <label className="form-label" htmlFor="custom-order-description">Describe your request *</label>
              <textarea id="custom-order-description" className="form-control" rows="5" value={form.description} onChange={(event) => updateFormField('description', event.target.value)} maxLength={5000} required aria-invalid={formErrors.description ? 'true' : undefined} aria-describedby={formErrors.description ? 'custom-order-description-error' : undefined} />
              <FieldError id="custom-order-description-error" message={formErrors.description} />
            </div>
            <div className="col-12">
              <label className="form-label" htmlFor="custom-order-reference">Product, reference, or inspiration (optional)</label>
              <input id="custom-order-reference" className="form-control" value={form.referenceText} onChange={(event) => updateFormField('referenceText', event.target.value)} maxLength={1000} placeholder="Paste a product link or describe the reference" aria-invalid={formErrors.referenceText ? 'true' : undefined} aria-describedby={formErrors.referenceText ? 'custom-order-reference-error' : undefined} />
              <FieldError id="custom-order-reference-error" message={formErrors.referenceText} />
            </div>
            <div className="col-12">
              <label className="form-label" htmlFor="custom-order-reference-image">Reference images (optional)</label>
              <input
                ref={referenceFileInput}
                id="custom-order-reference-image"
                className="form-control"
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleReferenceImageChange}
                aria-invalid={formErrors.referenceImage ? 'true' : undefined}
                aria-describedby="custom-order-reference-image-help custom-order-reference-image-error"
              />
              <div id="custom-order-reference-image-help" className="form-text">Upload up to {MAX_REFERENCE_IMAGES} images, with a combined size of {formatMegabytes(MAX_REFERENCE_TOTAL_BYTES)} or less. They will be kept with this request in Hunar's hosted file storage.</div>
              <FieldError id="custom-order-reference-image-error" message={formErrors.referenceImage} />
              {imageReading && <div className="form-text mt-2" role="status">Preparing images…</div>}
              {referenceImages.length > 0 && (
                <div className="custom-order-image-previews mt-3">
                  {referenceImages.map((image) => (
                    <div key={image.fileKey} className="custom-order-image-preview">
                      <img src={image.dataUrl} alt={`Selected reference: ${image.filename}`} />
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span className="small text-muted text-truncate">{image.filename}</span>
                        <button type="button" className="btn btn-sm btn-outline-custom" onClick={() => removeReferenceImage(image.fileKey)}>Remove</button>
                      </div>
                    </div>
                  ))}
                  <div className="custom-order-image-count form-text">{referenceImages.length}/{MAX_REFERENCE_IMAGES} images · {formatMegabytes(referenceImages.reduce((total, image) => total + image.size, 0))}/{formatMegabytes(MAX_REFERENCE_TOTAL_BYTES)}</div>
                </div>
              )}
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="custom-order-quantity">Quantity</label>
              <input id="custom-order-quantity" className="form-control" type="number" min="1" max="100" inputMode="numeric" value={form.quantity} onChange={(event) => updateFormField('quantity', event.target.value)} aria-invalid={formErrors.quantity ? 'true' : undefined} aria-describedby={formErrors.quantity ? 'custom-order-quantity-error' : undefined} />
              <FieldError id="custom-order-quantity-error" message={formErrors.quantity} />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="custom-order-budget">Budget (₹)</label>
              <input id="custom-order-budget" className="form-control" type="number" min="0" max="100000000" step="0.01" inputMode="decimal" value={form.budget} onChange={(event) => updateFormField('budget', event.target.value)} aria-invalid={formErrors.budget ? 'true' : undefined} aria-describedby={formErrors.budget ? 'custom-order-budget-help custom-order-budget-error' : 'custom-order-budget-help'} />
              <div id="custom-order-budget-help" className="form-text">Share the amount you are comfortable spending.</div>
              <FieldError id="custom-order-budget-error" message={formErrors.budget} />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label" htmlFor="custom-order-deadline">Suggested completion date (optional)</label>
              <input id="custom-order-deadline" className="form-control" type="date" min={getTodayIsoDate()} value={form.suggestedDeadline} onChange={(event) => updateFormField('suggestedDeadline', event.target.value)} aria-invalid={formErrors.suggestedDeadline ? 'true' : undefined} aria-describedby={formErrors.suggestedDeadline ? 'custom-order-deadline-error' : 'custom-order-deadline-help'} />
              <div id="custom-order-deadline-help" className="form-text">This helps us plan. We will confirm the actual timing with you.</div>
              <FieldError id="custom-order-deadline-error" message={formErrors.suggestedDeadline} />
            </div>
          </div>
              <button type="submit" className="btn btn-primary-custom mt-4" disabled={saving || imageReading}>{saving ? 'Sending request…' : 'Send request'}</button>
            </form>
          )}
        </>
      )}

      {requests.length > 0 && (
        <div className="border rounded bg-card p-3 mb-4 shadow-sm">
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div>
              <label className="form-label small fw-semibold mb-1" htmlFor="custom-order-status-filter">Status</label>
              <select id="custom-order-status-filter" className="form-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="declined">Declined</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="form-label small fw-semibold mb-1" htmlFor="custom-order-time-filter">Request age</label>
              <select id="custom-order-time-filter" className="form-select" value={timeFilter} onChange={(event) => setTimeFilter(event.target.value)}>
                {TIME_FILTERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            {(statusFilter !== 'all' || timeFilter !== 'all') && (
              <button type="button" className="btn btn-outline-custom" onClick={() => { setStatusFilter('all'); setTimeFilter('all'); }}>
                Clear filters
              </button>
            )}
            <span className="small text-muted ms-auto">Showing {filteredRequests.length} of {requests.length}</span>
          </div>
        </div>
      )}

      {requests.length === 0 ? (
        (!showRequestForm || adminView) && (
          <div className="border rounded bg-card p-5 text-center">
            <h2 className="h5 brand-font">No custom-order requests yet</h2>
            <p className="text-muted mb-0">{adminView ? 'New requests will appear here.' : 'Your request conversations will appear here after you send the first one.'}</p>
          </div>
        )
      ) : filteredRequests.length === 0 ? (
        <div className="border rounded bg-card p-5 text-center">
          <h2 className="h5 brand-font">No matching requests</h2>
          <p className="text-muted mb-0">Try a different status or request-age filter.</p>
        </div>
      ) : (
        <div className="row g-4">{filteredRequests.map((request) => <div className="col-12 col-lg-6" key={request.id}><RequestCard request={request} adminView={adminView} onDelete={handleDeleteRequest} /></div>)}</div>
      )}
    </div>
  );
}
